/*
 * Copyright (c) 2025. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import React, {useEffect, useState} from "react";
import {PackageItem} from "types/entities/PackageItem";
import {useTranslation} from "react-i18next";
import {useDispatch} from "useDispatch";
import {useTypedSelector} from "helpers/reducers";
import {useForm} from "react-hook-form";
import {getSelectedUpsell} from "utils/utility";
import {toast} from "react-toastify";
import BookingAction from "store/booking/BookingAction";
import {PackageItemPersonType} from "types/enums/PackageItemPersonType";
import clsx from "clsx";
import {
    GroupedPackageUpsellItemItemContainer,
} from "pages/Front/Booking/components/PackageUpsell/GroupedPackageUpsellItemItemContainer";

interface GroupedPackageUpsellItemItemProps {
    packageItem: PackageItem;
    isSeller?: boolean;
}

const GroupedPackageUpsellItemItem = (props: GroupedPackageUpsellItemItemProps) => {
    const {t} = useTranslation();
    const dispatch = useDispatch();
    const bookingProcess = useTypedSelector(state => state.bookingState.bookingProcess);
    const bookingFilter = useTypedSelector(state => state.bookingState.bookingProcess.filter);
    const {register, watch, setValue} = useForm();
    const upsellCount = watch("upsell-count");
    const [count, setCount] = useState(upsellCount);
    const [showRemoveButton, setShowRemoveButton] = useState(getSelectedUpsell(bookingProcess.upsell, props.packageItem) !== undefined);
    const [callingToAction, setCallingToAction] = useState(false);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const minimumValue = props.packageItem.extra.whole_group
        ? bookingFilter.adults + bookingFilter.children
        : props.packageItem.minimum_persons_count == 0
            ? 1
            : props.packageItem.minimum_persons_count
    ;
    const maximumDriversCount = props.packageItem.maximum_persons_count;
    const setOnChangeHandler = e => {
        const enteredValue = e.target.value;
        if (enteredValue < minimumValue) {
            setValueHandler(minimumValue);
            toast.error(t("cannot order less", {minimum_value: minimumValue}), {toastId: "cannotOrderless"});
            return;
        }
        if (enteredValue > maximumDriversCount) {
            setValueHandler(maximumDriversCount);
            return;
        }
        setValueHandler(enteredValue);
    };

    const setOnBlurHandler = (e: React.FocusEvent<HTMLInputElement>) => {
        const rawValue = e.target.value.trim();
        const enteredValue = Number(rawValue);

        if (rawValue === "" || isNaN(enteredValue) || enteredValue < minimumValue) {
            setCount(minimumValue);
            setValueHandler(minimumValue);
            toast.error(
                t("cannot order less", {minimum_value: minimumValue}),
                {toastId: "cannotOrderless"}
            );
            return;
        }
        if (enteredValue > maximumDriversCount) {
            setCount(maximumDriversCount);
            setValueHandler(maximumDriversCount);
            return;
        }
        setCount(enteredValue);
        setValueHandler(enteredValue);
    };

    const setValueHandler = value => {
        setIsLoading(true);
        const currentUpsell = getSelectedUpsell(bookingProcess.upsell, props.packageItem);

        setCount(value);
        setCallingToAction(true);
        if (value === 0 && currentUpsell) {
            dispatch(BookingAction.removeUpsell(currentUpsell));
            setIsLoading(false);
            return;
        }
        dispatch(BookingAction.setUpsell({packageItem: props.packageItem, upsell_option: 0, count: value}));
        setIsLoading(false);
    };

    function setDriversCountHandler(number: number) {
        const newCount = +count + number;
        if (newCount > maximumDriversCount) {
            setValueHandler(maximumDriversCount);
            toast.warning(t("We cannot take more people than: ") + maximumDriversCount, {toastId: "maximumDriverCount"});
            return;
        }

        if (newCount >= 0) {
            setValueHandler(newCount);
        }

        if (newCount < minimumValue) {
            setValueHandler(minimumValue);
            toast.error(t("cannot order less", {minimum_value: minimumValue}), {toastId: "minimumDriverCount"});
            return;
        }
    }

    useEffect(() => {
        let totalCount = props.packageItem.minimum_persons_count == 0
            ? 1
            : props.packageItem.minimum_persons_count;
        if (bookingFilter.adults > maximumDriversCount) {
            totalCount = maximumDriversCount;
        }

        setCount(totalCount);
        if (minimumValue > count) {
            setCount(minimumValue);
        }
        return () => {
        };
    }, [minimumValue]);

    useEffect(() => {
        const timer = setTimeout(() => {
            setCallingToAction(false);
        }, 500);
        return () => {
            clearTimeout(timer);
        };
    }, [callingToAction]);

    const setSelectedPackageUpsell = (): void => {
        setIsLoading(true);
        const currentUpsell = getSelectedUpsell(bookingProcess.upsell, props.packageItem);
        if (showRemoveButton && currentUpsell) {
            dispatch(BookingAction.removeUpsell(currentUpsell));
            setIsLoading(false);
            // toast.warning(t("Item removed"));

            setShowRemoveButton(false);
            return;
        }

        if (props.packageItem.extra.whole_group) {
            dispatch(
                BookingAction.pushUpsell({
                    packageItem: props.packageItem,
                    upsell_option: 0,
                    count: Number(count),
                }),
            );
            setIsLoading(false);
            setShowRemoveButton(true);
            // toast.success(t("Item added"));

            return;
        }
        if (count == 0 && currentUpsell) {
            dispatch(BookingAction.removeUpsell(currentUpsell));
            setIsLoading(false);
            setShowRemoveButton(false);
        }
        if (count > 0) {
            setShowRemoveButton(true);
            dispatch(
                BookingAction.pushUpsell({
                    packageItem: props.packageItem,
                    upsell_option: 0,
                    count: Number(count),
                }),
            );
            setIsLoading(false);
        }

        //upsell package goes before book
        // props.history.push("/booking/book/" + PackageItem.package_id );
    };
    const disabled = false;
    const valueButtonClassName =
        (disabled ? "text-gray-700 cursor-not-allowed" : "text-arbo-orange cursor-pointer") +
        " w-8 text-contrast-800 rounded-md hover:no-underline text-2xl text-center flex flex-row justify-center items-center leading-none";

    useEffect(() => {
        const currentUpsell = getSelectedUpsell(bookingProcess.upsell, props.packageItem);
        currentUpsell?.count && setCount(currentUpsell.count);
    }, [bookingProcess.upsell]);

    return (
        <GroupedPackageUpsellItemItemContainer
            callToAction={callingToAction}
            className={"GroupedPackageUpsellItemItem"}
            inputs={
                <div
                    className={clsx(props.packageItem.extra.whole_group && "was-hidden", "UpsellInputs flex flex-col md:flex-grow")}>
                    {props.packageItem.extra.max_count === 1 && (
                        <input {...register("upsell-count")} name={"upsell-count"} type={"hidden"}
                               value={props.packageItem.extra.max_count} />
                    )}
                    {props.packageItem.extra.max_count !== 1 && (
                        <label
                            className={clsx("GroupedPackageUpsellItemItemLabel flex flex-row justify-between items-center border-arbo-orange border-2 rounded-3xl h-[38px] xl:h-11 overflow-hidden")}>
                            <span className={"sr-only"}>{t("Count")}</span>
                            <button className={valueButtonClassName} type={"button"}
                                    onClick={() => setDriversCountHandler(-1)}>
                                <span>-</span>
                            </button>
                            <input
                                {...register("upsell-count")}
                                className={
                                    "font-bold w-10 text-gray-900 hover:appearance-none focus:outline-none text-center text-lg md:text-xl py-1.5"
                                }
                                inputMode={"numeric"}
                                max={props.packageItem.extra.max_count}
                                min={minimumValue}
                                name={"upsell-count"}
                                placeholder={
                                    props.packageItem.person_type === PackageItemPersonType.Adult ? "" + bookingFilter.adults : "" + bookingFilter.children
                                }
                                type={"text"}
                                value={count}
                                // onChange={setOnChangeHandler}
                                onChange={e => setCount(Number(e.target.value))}
                                onBlur={setOnBlurHandler}
                            />
                            <button className={valueButtonClassName} type={"button"}
                                    onClick={() => setDriversCountHandler(+1)}>
                                <span>+</span>
                            </button>
                        </label>
                    )}
                </div>
            }
            isLoading={isLoading}
            packageItem={props.packageItem}
            setSelectedPackageUpsell={setSelectedPackageUpsell}
            showRemoveButton={showRemoveButton}
            style={{}}
            quantity={count}
            isSeller={props.isSeller}
        />
    );
};

export default GroupedPackageUpsellItemItem;